/**
 * ChaseCamera.ts — camera director for the player Boat.
 *
 * Three modes:
 *   - "external": user-orbited 3rd-person view. Camera target lerps to follow
 *     the boat; az/el/dist are owned by OrbitalCameraController.
 *   - "cockpit":  1st-person view from the helm. Camera position is locked
 *     to a body-frame point on the boat; the user can freely look around
 *     (pointer drag → yaw + pitch offsets relative to the bow), and the
 *     view stays bolted to the hull when the boat yaws — exactly what a
 *     helmsman experiences leaning on the wheel.
 *   - "free":     no boat following at all. User has full orbital control.
 *
 * Cockpit "feel" notes:
 *   - The eye position inherits a fraction of the boat's pitch (bow lifts
 *     → eye lifts), so chop and acceleration are felt without the
 *     nausea-inducing full horizon tilt of true world-locked-to-deck cams.
 *   - The eye position is exponentially smoothed (~80 ms), filtering out
 *     FFT micro-jitter while preserving heave + body roll.
 *   - FOV widens to ~72° in cockpit; tunnel-vision 60° is wrong when you
 *     "are" in the cockpit instead of zoomed in from outside.
 */

import type { Camera } from "./Camera";
import type { Boat } from "./sim/Boat";
import type { CameraMode } from "./State";

export interface ChaseCameraOptions {
  /** Lerp factor per second (~ 1/timeConstant). */
  followLerp?: number;
  /** Minimum world-space camera Y above water level. */
  minHeightAboveWater?: number;
  /** Body-frame cockpit offset (forward, up, right). */
  cockpitOffset?: [number, number, number];
  /** Keep syncing cockpit offset from the boat's preset anchors each frame. */
  syncCockpitOffsetFromBoatAnchors?: boolean;
  /** FOV (degrees) used while in cockpit mode. */
  cockpitFovDeg?: number;
  /** Max yaw the helmsman can swivel his head from straight-ahead (rad). */
  cockpitMaxYaw?: number;
  /** Max pitch (rad) — limits looking at the deck / sky. */
  cockpitMaxPitch?: number;
  /** Fraction of boat pitch fed into cockpit eye position (0..1). */
  cockpitPitchFollow?: number;
}

export class ChaseCamera {
  readonly followLerp: number;
  readonly minHeightAboveWater: number;
  /** Cockpit offset in body frame (X-fwd, Y-up, Z-stbd). Mutable so devs
   *  can hot-tune from the console when preset-anchor syncing is enabled. */
  cockpitOffset: [number, number, number];
  readonly cockpitFov: number;
  readonly cockpitMaxYaw: number;
  readonly cockpitMaxPitch: number;
  readonly cockpitPitchFollow: number;
  private readonly syncCockpitOffsetFromBoatAnchors: boolean;
  enabled = true;
  mode: CameraMode = "external";

  // Saved external-mode state when temporarily switching to cockpit/free.
  private savedAz = 0;
  private savedEl = 0;
  private savedDist = 0;
  private savedFov = 0;
  private savedTarget: [number, number, number] = [0, 0, 0];
  private hasSaved = false;

  // Cockpit working state.
  private cockpitInitialized = false;
  /** Helmsman head yaw offset relative to boat-forward (rad). */
  private lookYaw = 0;
  /** Helmsman head pitch offset (rad). */
  private lookPitch = 0;
  /** Last camera az/el observed in cockpit, used to detect user drag. */
  private prevAzimuth = 0;
  private prevElevation = 0;
  /** Smoothed cockpit-eye world position (low-pass over micro-jitter). */
  private smoothedEye: [number, number, number] = [0, 0, 0];
  private smoothedEyeValid = false;

  constructor(
    private camera: Camera,
    private boat: Boat,
    opts: ChaseCameraOptions = {},
  ) {
    this.followLerp = opts.followLerp ?? 4.0;
    this.minHeightAboveWater = opts.minHeightAboveWater ?? 0.6;
    // Cockpit aft of cabin (cabin spans body-X ≈ [-2.1, +1.1]); +1.6 m up
    // (eye height standing in the cockpit), 0 lateral.
    this.cockpitOffset = opts.cockpitOffset ?? [-3.6, 1.6, 0];
    this.syncCockpitOffsetFromBoatAnchors =
      opts.syncCockpitOffsetFromBoatAnchors ?? opts.cockpitOffset === undefined;
    this.cockpitFov = ((opts.cockpitFovDeg ?? 72) * Math.PI) / 180;
    this.cockpitMaxYaw = opts.cockpitMaxYaw ?? (Math.PI * 3) / 4; // ±135°
    this.cockpitMaxPitch = opts.cockpitMaxPitch ?? Math.PI * 0.45; // ±81°
    this.cockpitPitchFollow = opts.cockpitPitchFollow ?? 0.3;
  }

  /**
   * Recenter the helmsman's gaze to straight-ahead (along the bow).
   * Bound to "C" in the global key handler.
   */
  recenterCockpitView(): void {
    this.lookYaw = 0;
    this.lookPitch = 0;
  }

  setMode(mode: CameraMode): void {
    if (mode === this.mode) return;
    const cam = this.camera;
    if (this.mode === "external" && mode !== "external") {
      this.savedAz = cam.azimuth;
      this.savedEl = cam.elevation;
      this.savedDist = cam.distance;
      this.savedFov = cam.fovY;
      this.savedTarget = [cam.target[0]!, cam.target[1]!, cam.target[2]!];
      this.hasSaved = true;
    }
    // Restore saved state on any return to a non-cockpit mode — prevents
    // free-fly from inheriting cockpit's tiny distance (2 m) and 72° FOV.
    if (mode !== "cockpit" && this.hasSaved) {
      cam.azimuth = this.savedAz;
      cam.elevation = this.savedEl;
      cam.distance = this.savedDist;
      cam.fovY = this.savedFov;
      cam.target[0] = this.savedTarget[0]!;
      cam.target[1] = this.savedTarget[1]!;
      cam.target[2] = this.savedTarget[2]!;
      if (mode === "external") this.hasSaved = false;
    }
    this.mode = mode;
    if (mode === "cockpit") {
      // Distance is mostly cosmetic in cockpit (we override the target each
      // frame so the camera lands exactly on the cockpit eye), but keep it
      // small + non-zero so the orbital math stays well-conditioned.
      cam.distance = 2.0;
      cam.fovY = this.cockpitFov;
      this.cockpitInitialized = false;
      this.smoothedEyeValid = false;
      this.lookYaw = 0;
      this.lookPitch = 0;
    }
  }

  step(dt: number, waterLevel: number): void {
    if (!this.enabled) return;
    const cam = this.camera;
    const b = this.boat;

    if (this.mode === "free" || this.mode === "flyby") return;

    if (this.mode === "external") {
      const lag = 1 - Math.exp(-dt * this.followLerp);
      cam.target[0]! += (b.posX - cam.target[0]!) * lag;
      cam.target[1]! += (waterLevel + 0.5 - cam.target[1]!) * lag;
      cam.target[2]! += (b.posZ - cam.target[2]!) * lag;
      cam.recompute();
      const minY = waterLevel + this.minHeightAboveWater;
      if (cam.position[1]! < minY) {
        cam.target[1]! += minY - cam.position[1]!;
        cam.recompute();
      }
      return;
    }

    // ----- mode === "cockpit" --------------------------------------------
    // 1) Detect user pointer-drag on the orbital controller. The controller
    //    writes az/el directly; we attribute any change since last frame
    //    to the helmsman turning his head, accumulating it into
    //    lookYaw/lookPitch (which are stored in BOAT-relative coords, so
    //    they survive boat yawing).
    if (this.cockpitInitialized) {
      this.lookYaw += cam.azimuth - this.prevAzimuth;
      this.lookPitch += cam.elevation - this.prevElevation;
    }
    // Yaw is unclamped — the skipper can turn his whole body during
    // manoeuvres. Wrap to [-π, π] to keep the accumulator bounded.
    if (this.lookYaw > Math.PI || this.lookYaw < -Math.PI) {
      this.lookYaw =
        ((((this.lookYaw + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) %
          (2 * Math.PI)) -
        Math.PI;
    }
    // Pitch stays clamped to plausible neck range.
    if (this.lookPitch > this.cockpitMaxPitch)
      this.lookPitch = this.cockpitMaxPitch;
    else if (this.lookPitch < -this.cockpitMaxPitch)
      this.lookPitch = -this.cockpitMaxPitch;

    // 2) Compute cockpit eye position in world space, with a fraction of
    //    the boat's pitch so the helmsman feels the bow lift on chop.
    const ch = Math.cos(b.heading);
    const sh = Math.sin(b.heading);
    const fwdX = sh,
      fwdZ = -ch;
    const rgtX = ch,
      rgtZ = sh;
    // Pull the freshest body-frame offset from preset anchors only when the
    // caller opted in. A selected GLB anchor like `Skipper.Head` stays fixed.
    const live = this.syncCockpitOffsetFromBoatAnchors
      ? b.params.anchors?.cameraCockpit
      : undefined;
    if (live) {
      this.cockpitOffset[0] = live[0];
      this.cockpitOffset[1] = live[1];
      this.cockpitOffset[2] = live[2];
    }
    const [ox, oyRaw, oz] = this.cockpitOffset;
    // Rotate body-frame (X-fwd, Y-up) part of the offset by (pitch * follow).
    // Pitch +ve raises bow (+X end up); rotation about boat-right axis.
    const pitchEff = b.pitch * this.cockpitPitchFollow;
    const cp = Math.cos(pitchEff);
    const sp = Math.sin(pitchEff);
    const rotOx = ox * cp - oyRaw * sp;
    const rotOy = ox * sp + oyRaw * cp;
    const targetEyeX = b.posX + fwdX * rotOx + rgtX * oz;
    const targetEyeY = b.posY + rotOy;
    const targetEyeZ = b.posZ + fwdZ * rotOx + rgtZ * oz;

    // 3) Low-pass the eye position to suppress sub-cm FFT jitter while
    //    preserving heave & body roll. τ ≈ 80 ms.
    const k = 1 - Math.exp(-dt / 0.08);
    if (!this.smoothedEyeValid) {
      this.smoothedEye[0] = targetEyeX;
      this.smoothedEye[1] = targetEyeY;
      this.smoothedEye[2] = targetEyeZ;
      this.smoothedEyeValid = true;
    } else {
      this.smoothedEye[0] += (targetEyeX - this.smoothedEye[0]) * k;
      this.smoothedEye[1] += (targetEyeY - this.smoothedEye[1]) * k;
      this.smoothedEye[2] += (targetEyeZ - this.smoothedEye[2]) * k;
    }
    const eyeX = this.smoothedEye[0];
    const eyeY = this.smoothedEye[1];
    const eyeZ = this.smoothedEye[2];

    // 4) Re-apply azimuth = boat-forward yaw + helmsman's head yaw, then
    //    set target so that camera position lands exactly on the eye.
    cam.azimuth = -b.heading + this.lookYaw;
    cam.elevation = this.lookPitch;
    const d = cam.distance > 0.1 ? cam.distance : 2.0;
    const ce = Math.cos(cam.elevation);
    const se = Math.sin(cam.elevation);
    const sa = Math.sin(cam.azimuth);
    const ca = Math.cos(cam.azimuth);
    // Camera.recompute(): position = target + d*(ce*sa, se, ce*ca).
    // We want position = eye, so target = eye - offset.
    cam.target[0] = eyeX - d * ce * sa;
    cam.target[1] = eyeY - d * se;
    cam.target[2] = eyeZ - d * ce * ca;

    cam.recompute();

    this.prevAzimuth = cam.azimuth;
    this.prevElevation = cam.elevation;
    this.cockpitInitialized = true;
  }
}
